/*
 * Layout  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useCallback, useRef } from "react";
import { Bem, BEM, T } from "@adapptio/lib-components";
import { LayoutState_Tabs } from "./LayoutState";
import { LAYOUT_TABS_STYLE, TLayoutActivateHandler, TLayoutRenderComponentFn } from "./LayoutTypes";
import { useLayoutElement } from "./useLayoutElement";
import { LayoutResizableElement } from "./LayoutResizableElement";
import { Icon, ICON_SIZE } from "../Icon";
import { Tooltip } from "../Tooltip";
import { LayoutContextProvider } from "./LayoutContext";
import { Toolbar, TOOLBAR_STYLE } from "../Toolbar/Toolbar";
import { Tabs, TABS_STYLE } from "../Tabs";
// import { TabbedContainer, TABBED_CONTAINER_TYPE } from "../TabbedContainer";
// import { render } from "react-dom";
import { ToolbarSpacer } from "../Toolbar/ToolbarSpacer";

export interface ILayoutTabsNavItemProps {
	id?: string;
	label?: string;
	icon?: string;
	tooltip?: string;
	onClick: (e) => void;
	isActive: boolean;
}

export interface ILayoutTabsNavProps {
	el: LayoutState_Tabs;
	baseClass: Bem;
	styles: LAYOUT_TABS_STYLE;
	renderComponent: TLayoutRenderComponentFn;
}

const LayoutTabsNavItem: FC<ILayoutTabsNavItemProps> = ({ id, label, icon, tooltip, onClick, isActive }) => {
	const rootElRef = useRef<HTMLButtonElement>();

	const baseClass = BEM("edt-layout", "element-tabs-nav-item", {
		"is-active": isActive,
		"icon-only": !label
	});

	baseClass.add("m-layout-tab-nav-id-" + id);

	return (
		<button className={baseClass.toClassName()} onClick={onClick} ref={rootElRef}>
			<span className={baseClass.element("element-tabs-nav-item-corner", "left").toClassName()} />
			<span className={baseClass.element("element-tabs-nav-item-corner", "right").toClassName()} />
			{icon ? <Icon name={icon} size={ICON_SIZE.CUSTOM} /> : null}
			{label ? (
				<span className={baseClass.element("element-tabs-nav-item-label").toClassName()}>
					<T domain="editor" term={label} />
				</span>
			) : null}
			{tooltip ? (
				<Tooltip for={rootElRef} openDelay={1200}>
					<T domain="editor" term={tooltip} />
				</Tooltip>
			) : null}
		</button>
	);
};

const LayoutTabsNav: FC<ILayoutTabsNavProps> = ({ el, baseClass, styles, renderComponent }) => {
	const handleSelect = (e, index: number) => {
		if (el.isExpanded) {
			e.stopPropagation();
		}

		el.selectTab(index);
	};

	const selectItem = (tabIndex, e) => {
		handleSelect(
			e,
			el.tabs.findIndex((item) => item.id === tabIndex)
		);
	};

	if (styles === LAYOUT_TABS_STYLE.EXPAND) {
		return (
			<nav className={baseClass.element("element-tabs-nav").add("edt-base").toClassName()}>
				{el.tabs.map((tab, index) => {
					return (
						<LayoutTabsNavItem
							key={tab.id}
							id={tab.id}
							label={tab.label}
							icon={tab.icon}
							tooltip={tab.tooltip}
							onClick={(e) => handleSelect(e, index)}
							isActive={el.currentTabIndex === index}
						/>
					);
				})}
			</nav>
		);
	} else if (styles === LAYOUT_TABS_STYLE.BUTTON) {
		return (
			<>
				<Tabs
					className={baseClass.element("main-tabs").toClassName()}
					items={el.tabs}
					renderComponent={renderComponent}
					selectedId={el.tabs[el.currentTabIndex].id}
					onSelect={selectItem}
					style={TABS_STYLE.COMPACT}
				/>
				<ToolbarSpacer grow={1} />
				{el.tabs[el.currentTabIndex]?.additionalTabs &&
					renderComponent(el.tabs[el.currentTabIndex].additionalTabs)}
			</>
		);
	}
};

export interface ILayoutTabsProps {
	state: LayoutState_Tabs;
	renderComponent: TLayoutRenderComponentFn;
	onActivate?: TLayoutActivateHandler;
}

export const LayoutTabs: FC<ILayoutTabsProps> = ({ state, renderComponent, onActivate }) => {
	const el = useLayoutElement(state);
	const isExpanded = !el.isExpandable || (el.isExpandable && el.isExpanded);

	const layoutElRef = useRef<HTMLDivElement>();

	const handleFocus = useCallback(() => {
		if (onActivate) {
			onActivate(el);
		}
	}, [ onActivate ]);

	const handleToggleExpanded = useCallback(() => {
		el.toggleExpanded();
	}, [ el ]);

	const baseClass = BEM("edt-layout", "element", {
		"type-tabs": true,
		"is-expandable": el.isExpandable,
		"is-expanded": el.isExpandable && el.isExpanded,
		"is-collapsed": el.isExpandable && !el.isExpanded
	});

	baseClass.add("m-layout-tabs-" + el.id);

	const style = {};

	if (typeof el.size === "string") {
		style["width"] = el.size;
	} else {
		style["flexGrow"] = el.size;
	}

	if (!isExpanded && el.renderWhenCollapsed === false) {
		return <div className={baseClass.toClassName()} style={style}></div>;
	}

	// const [ activeTab, setActiveTab ] = useState(el.tabs[0].id);

	return (
		<LayoutResizableElement
			state={state}
			className={baseClass.toClassName()}
			style={style}
			onFocus={handleFocus}
			tabIndex={0}
			minSize={200}
			ref={layoutElRef}
		>
			<LayoutContextProvider state={state} layoutElRef={layoutElRef}>
				<Toolbar
					expanded={isExpanded}
					expandable={!!el.arrowPosition}
					arrowPosition={el.arrowPosition}
					style={TOOLBAR_STYLE.TABS}
					onToggleExpanded={handleToggleExpanded}
				>
					<LayoutTabsNav
						el={el}
						baseClass={baseClass}
						styles={el.styles}
						renderComponent={renderComponent}
					/>
				</Toolbar>
				<div className={baseClass.element("element-tabs-content").toClassName()}>
					{el.tabs.map((tab, index) => {
						const itemClass = baseClass.element("element-tabs-content-item", {
							"is-active": el.currentTabIndex === index
						});
						itemClass.add("m-layout-tab-cnt-id-" + tab.id);

						return (
							<div key={tab.id} className={itemClass.toClassName()}>
								{el.currentTabIndex === index ||
								(el.currentTabIndex !== index && tab.renderWhenHidden !== false)
									? renderComponent(tab.contentComponent)
									: null}
							</div>
						);
					})}
				</div>
			</LayoutContextProvider>
		</LayoutResizableElement>
	);
};
